import { useEffect } from 'react';
import { navigate } from '@/hooks/useRoute';
import { MEDIA } from '@/data/site';
import { useStore } from '@/context/StoreContext';
import './DiamondEducationPage.css';

const clarityGrades = [
  { code:'FL', title:'Flawless', text:'ללא תכלילים או פגמים הנראים בהגדלה ×10.' },
  { code:'IF', title:'Internally Flawless', text:'ללא תכלילים פנימיים; ייתכנו סימנים חיצוניים זעירים בהגדלה ×10.' },
  { code:'VVS1–VVS2', title:'Very Very Slightly Included', text:'תכלילים זעירים שקשה מאוד לזהות גם בהגדלה ×10.' },
  { code:'VS1–VS2', title:'Very Slightly Included', text:'תכלילים קלים; בדרך כלל אינם בולטים לעין ללא הגדלה.' },
  { code:'SI1–SI2', title:'Slightly Included', text:'תכלילים מורגשים יותר בהגדלה ×10, ולעיתים עשויים להיות נראים גם ללא הגדלה.' },
  { code:'I1–I3', title:'Included', text:'תכלילים ברורים יותר, שעלולים להשפיע גם על המראה, השקיפות או העמידות.' },
];

const shapes = [
  { key:'round', name:'עגול', note:'הקלאסיקה של הברק; פרופורציות סימטריות ומראה על־זמני.' },
  { key:'oval', name:'אובל', note:'מראה מוארך ורך שמעניק נוכחות גדולה יחסית על האצבע.' },
  { key:'cushion', name:'קושן', note:'ריבוע מעוגל עם אופי רומנטי ושילוב בין רכות לנוכחות.' },
  { key:'princess', name:'פרינסס', note:'קווים מרובעים חדים, מראה מודרני וברק דינמי.' },
  { key:'emerald', name:'אמרלד', note:'חיתוך מדרגות נקי ואלגנטי שמדגיש שקיפות וגיאומטריה.' },
  { key:'radiant', name:'רדיאנט', note:'מסגרת גיאומטרית עם ברק אינטנסיבי יותר מחיתוכי מדרגות.' },
  { key:'marquise', name:'מרקיזה', note:'צורה ארוכה ומחודדת שיוצרת אשליה של אבן גדולה ומוארכת.' },
  { key:'pear', name:'טיפה', note:'שילוב בין עגול למרקיזה — רך מצד אחד וחד מצד שני.' },
  { key:'heart', name:'לב', note:'בחירה רומנטית ואישית שמצריכה סימטריה טובה במיוחד.' },
];

const faqs = [
  ['מהם 4 ה־C של יהלום?','Carat, Cut, Color ו־Clarity — משקל, איכות חיתוך, צבע וניקיון. יחד הם נותנים שפה מקצועית להשוואה בין יהלומים.'],
  ['האם קראט הוא גודל?','לא בדיוק. קראט הוא יחידת משקל. שני יהלומים באותו משקל יכולים להיראות שונים בגודל הפנים בגלל הצורה והפרופורציות.'],
  ['למה החיתוך כל כך חשוב?','החיתוך קובע כיצד האור נכנס ליהלום, מוחזר ממנו ומתפזר. לכן הוא משפיע ישירות על בהירות, אש ונצנוץ.'],
  ['מה המשמעות של ניקיון?','ניקיון מתאר את מידת הנוכחות של תכלילים פנימיים וסימנים חיצוניים, כפי שנבחנים בתנאים גמולוגיים ובהגדלה.'],
  ['מה נותן דו״ח גמולוגי?','הדו״ח מתעד את מאפייני האבן שנבדקו במעבדה — כגון מידות, משקל, צבע, ניקיון ונתונים נוספים. ביהלום מעבדה חשוב שהדו״ח יציין במפורש את מקורו המעבדתי.'],
  ['איזו צורת יהלום כדאי לבחור?','אין צורה אחת נכונה. מתחילים מהסגנון שאוהבים, בודקים כיצד האבן נראית על היד, ואז מאזנים בין פרופורציות, תקציב ושאר נתוני האיכות.'],
];

function go(path:string){ navigate(path); }

export function DiamondEducationPage(){
  const {cms}=useStore();
  useEffect(()=>{
    const previousTitle=document.title;
    document.title='מדריך יהלומים מקצועי | MIELLEY';
    return ()=>{ document.title=previousTitle; };
  },[]);

  const whatsappText=encodeURIComponent('שלום MIELLEY, אשמח לייעוץ בבחירת יהלום לפי 4C.');
  const whatsapp=`https://wa.me/${cms.site.whatsapp}?text=${whatsappText}`;

  return <>
    <nav className="breadcrumbs container">
      <a href="/" onClick={e=>{e.preventDefault();go('/')}}>בית</a>
      <span>›</span>
      <strong>מרכז הידע ליהלומים</strong>
    </nav>

    <main className="diamond-education" dir="rtl">
      <section className="de-hero">
        <div className="container de-hero-grid">
          <div className="de-hero-copy">
            <span className="de-overline">MIELLEY DIAMOND KNOWLEDGE</span>
            <h1>להבין יהלום.<br/>לבחור בביטחון.</h1>
            <p>לא צריך להיות גמולוג כדי לבחור נכון. צריך לדעת אילו נתונים באמת משנים את המראה, איך לקרוא אותם — ואיפה נכון להשקיע את התקציב.</p>
            <div className="de-hero-actions">
              <a className="btn gold" href="#four-c">להתחיל מה־4C</a>
              <a className="btn secondary" href="/appointments" onClick={e=>{e.preventDefault();go('/appointments')}}>ייעוץ אישי</a>
            </div>
            <div className="de-hero-facts">
              <span><b>4</b> מדדי איכות מרכזיים</span>
              <span><b>11</b> דרגות ניקיון בסולם GIA</span>
              <span><b>D–Z</b> סולם צבע ליהלומים חסרי צבע עד גוון קל</span>
            </div>
          </div>

          <div className="de-hero-visual">
            <img src={MEDIA.diamondExpertise || MEDIA.heroEngagementV711} alt="מרכז הידע ליהלומים של MIELLEY"/>
            <div className="de-hero-card">
              <span>THE 4C COMPASS</span>
              <strong>CARAT · CUT · COLOR · CLARITY</strong>
              <small>ארבעה נתונים. החלטה אחת מדויקת יותר.</small>
            </div>
          </div>
        </div>
      </section>

      <nav className="de-index" aria-label="ניווט במדריך">
        <div className="container">
          <a href="#four-c">4C</a>
          <a href="#clarity">ניקיון</a>
          <a href="#shapes">צורות</a>
          <a href="#certificate">דו״ח גמולוגי</a>
          <a href="#choose">איך בוחרים</a>
          <a href="#diamond-faq">שאלות נפוצות</a>
        </div>
      </nav>

      <section className="de-intro">
        <div className="container de-intro-grid">
          <div>
            <span className="de-kicker">THE LANGUAGE OF DIAMONDS</span>
            <h2>לפני המחיר — מבינים מה עומד מאחוריו.</h2>
          </div>
          <div>
            <p>ה־4C הם המסגרת המקובלת לתיאור איכות יהלום: <strong>משקל בקראט, חיתוך, צבע וניקיון</strong>. הם מאפשרים להשוות בין אבנים באופן מסודר במקום להסתמך רק על רושם ראשוני.</p>
            <p>ביהלומי מעבדה אותם עקרונות איכות נשארים רלוונטיים, ובדו״ח גמולוגי מקצועי מצוין גם שמדובר ביהלום שגודל במעבדה.</p>
          </div>
        </div>
      </section>

      <section className="de-fourc" id="four-c">
        <div className="container">
          <header className="de-section-head">
            <span>01 · THE FOUR C'S</span>
            <h2>ארבעת הנתונים שמספרים את סיפור היהלום</h2>
            <p>אף C לא עומד לבד. יהלום מוצלח הוא איזון בין הנתונים, המראה שאתם אוהבים והתקציב.</p>
          </header>

          <div className="de-c-grid">
            <article className="de-c-card de-carat">
              <div className="de-c-number">01</div>
              <span>CARAT</span>
              <h3>קראט · משקל</h3>
              <p>קראט מודד <strong>משקל</strong>, לא קוטר. קראט אחד שווה 0.2 גרם. שני יהלומים באותו משקל יכולים להיראות שונים בגודל בגלל פרופורציות וצורה.</p>
              <div className="de-carat-visual" aria-hidden="true">
                <i className="ct05">0.50</i><i className="ct10">1.00</i><i className="ct15">1.50</i><i className="ct20">2.00</i>
              </div>
              <div className="de-note"><b>טיפ MIELLEY</b><span>אל תעצרו במספר עגול. לפעמים אבן מעט מתחת לסף קראט מסוים נותנת מראה דומה — עם יותר גמישות בתקציב.</span></div>
            </article>

            <article className="de-c-card de-cut">
              <div className="de-c-number">02</div>
              <span>CUT</span>
              <h3>חיתוך · האור שבפנים</h3>
              <p>חיתוך אינו צורת האבן. הוא מתאר עד כמה הפרופורציות, הסימטריה והליטוש מאפשרים לאור לחזור לעין בצורה מרשימה.</p>
              <div className="de-light-triad">
                <div><b>BRIGHTNESS</b><span>בהירות</span></div>
                <div><b>FIRE</b><span>פיזור צבע</span></div>
                <div><b>SCINTILLATION</b><span>נצנוץ בתנועה</span></div>
              </div>
              <div className="de-grade-line"><span>Excellent</span><span>Very Good</span><span>Good</span><span>Fair</span><span>Poor</span></div>
              <div className="de-note"><b>מה חשוב לדעת</b><span>בדירוג GIA, ציון Cut רשמי ניתן ליהלום עגול בריליאנט סטנדרטי; בצורות Fancy מסתכלים גם על פרופורציות ומראה בפועל.</span></div>
            </article>

            <article className="de-c-card de-color">
              <div className="de-c-number">03</div>
              <span>COLOR</span>
              <h3>צבע · כמה פחות, לפעמים יותר</h3>
              <p>ביהלומים בטווח D–Z מודדים למעשה את מידת היעדר הצבע. D היא הדרגה חסרת הצבע ביותר, ובהמשך מופיעים בהדרגה גוונים צהבהבים או חומים.</p>
              <div className="de-color-scale">
                <div className="de-color-letters">{'DEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(x=><span key={x}>{x}</span>)}</div>
                <div className="de-color-bar"/>
                <div className="de-color-groups"><span>D–F · חסרי צבע</span><span>G–J · כמעט חסרי צבע</span><span>K–M · גוון קל</span><span>N–Z · גוון מורגש יותר</span></div>
              </div>
              <div className="de-note"><b>בחירה חכמה</b><span>הבדלים בין דרגות סמוכות יכולים להיות עדינים מאוד לעין, ולכן כדאי לבדוק את האבן גם ביחס לצבע המתכת של הטבעת.</span></div>
            </article>

            <article className="de-c-card de-clarity">
              <div className="de-c-number">04</div>
              <span>CLARITY</span>
              <h3>ניקיון · מה רואים, ומה רק במיקרוסקופ</h3>
              <p>ניקיון בוחן תכלילים פנימיים וסימנים חיצוניים. הדירוג נקבע בהגדלה ×10 ומתחשב בין היתר בגודל, במספר, במיקום ובנראות של המאפיינים.</p>
              <div className="de-clarity-mini">
                <span>FL</span><span>IF</span><span>VVS</span><span className="active">VS</span><span>SI</span><span>I</span>
              </div>
              <div className="de-note"><b>לא תמיד צריך את הדרגה הגבוהה ביותר</b><span>יש יהלומים בדרגות VS — ולעיתים גם SI בהתאם לאבן — שנראים נקיים לעין ללא הגדלה. הבחירה צריכה להיעשות לפי האבן עצמה, לא רק לפי האותיות.</span></div>
            </article>
          </div>
        </div>
      </section>

      <section className="de-clarity-section" id="clarity">
        <div className="container">
          <header className="de-section-head compact">
            <span>02 · CLARITY SCALE</span>
            <h2>סולם הניקיון — מה־FL ועד I3</h2>
            <p>אותן אותיות שאתם רואים בדו״ח הגמולוגי מקבלות כאן משמעות ברורה.</p>
          </header>

          <div className="de-clarity-scale">
            {clarityGrades.map((grade,i)=><article key={grade.code}>
              <div className="de-grade-orb"><span>{String(i+1).padStart(2,'0')}</span></div>
              <div>
                <strong>{grade.code}</strong>
                <small>{grade.title}</small>
                <p>{grade.text}</p>
              </div>
            </article>)}
          </div>
          <p className="de-fineprint">הדירוג נקבע במעבדה לפי בדיקה מקצועית. “נקי לעין” הוא תיאור שימושי לצרכן, אך אינו דרגת ניקיון רשמית בפני עצמו.</p>
        </div>
      </section>

      <section className="de-shapes" id="shapes">
        <div className="container">
          <header className="de-section-head">
            <span>03 · SHAPE DIRECTORY</span>
            <h2>צורת היהלום משנה את כל האופי</h2>
            <p>צורה היא בחירה עיצובית. היא משפיעה על הפרופורציות, התחושה על היד והאופן שבו האבן מתקשרת עם העיצוב.</p>
          </header>

          <div className="de-shape-grid">
            {shapes.map(shape=><article key={shape.key}>
              <div className={`de-shape-icon ${shape.key}`} aria-hidden="true"><i/></div>
              <h3>{shape.name}</h3>
              <p>{shape.note}</p>
            </article>)}
          </div>
        </div>
      </section>

      <section className="de-certificate" id="certificate">
        <div className="container de-certificate-grid">
          <div className="de-report-card">
            <span>DIAMOND GRADING REPORT</span>
            <div className="de-report-diamond">◇</div>
            <strong>VERIFIED CHARACTERISTICS</strong>
            <small>Measurements · Carat · Color · Clarity · Cut / Proportions · Identification</small>
          </div>
          <div className="de-certificate-copy">
            <span className="de-kicker">04 · CERTIFICATE & QUALITY</span>
            <h2>לא “תעודה יפה” — אלא מסמך שמאפשר לבדוק את הנתונים.</h2>
            <p>דו״ח גמולוגי ממעבדה מוכרת מתעד את מאפייני היהלום שנבדקו באופן בלתי תלוי. בין המעבדות המוכרות בעולם נמצאות GIA ו־IGI.</p>
            <ul>
              <li><b>זהות האבן:</b> מידות, משקל ונתוני זיהוי.</li>
              <li><b>4C:</b> צבע, ניקיון, משקל בקראט ונתוני חיתוך/פרופורציות בהתאם לסוג האבן והדו״ח.</li>
              <li><b>יהלום מעבדה:</b> חשוב שהדו״ח יציין במפורש שהאבן Laboratory Grown.</li>
              <li><b>אימות:</b> מספר דו״ח מאפשר לבדוק את הרשומה מול המעבדה המנפיקה.</li>
            </ul>
            <p className="de-fineprint light">דו״ח דירוג גמולוגי מתאר מאפיינים; הוא אינו בהכרח הערכת שווי כספית או תחליף לשמאות לצורכי ביטוח.</p>
          </div>
        </div>
      </section>

      <section className="de-choose" id="choose">
        <div className="container">
          <header className="de-section-head">
            <span>05 · THE MIELLEY METHOD</span>
            <h2>כך בוחרים בלי ללכת לאיבוד במספרים</h2>
          </header>

          <div className="de-steps">
            <article><b>01</b><h3>מתחילים מהמראה</h3><p>עגול, אובל, אמרלד או קושן? בחרו קודם את האופי שאתם אוהבים ואת סגנון הטבעת.</p></article>
            <article><b>02</b><h3>מגדירים תקציב אמיתי</h3><p>לא מחפשים “את הציון הכי גבוה” בכל שדה. מחלקים את התקציב לפי מה שבאמת נראה לעין ומשנה לכם.</p></article>
            <article><b>03</b><h3>מאזנים את ה־4C</h3><p>בודקים חיתוך, צבע וניקיון לצד הקראט. לפעמים שינוי קטן באחד הנתונים מאפשר קפיצה משמעותית באחר.</p></article>
            <article><b>04</b><h3>מוודאים מול דו״ח ואבן</h3><p>עוברים על הדו״ח הגמולוגי ובוחנים את האבן עצמה — כי הנתונים מסבירים את היהלום, אבל העין בוחרת אותו.</p></article>
          </div>

          <div className="de-buying-note">
            <div><span>MIELLEY NOTE</span><h3>המספרים הם מסננת. היופי הוא ההחלטה.</h3></div>
            <p>שני יהלומים עם נתונים דומים יכולים להיראות אחרת. לכן הבחירה הסופית צריכה לשלב מידע מקצועי, צפייה באבן והתאמה לתכשיט שבו היא תשובץ.</p>
          </div>
        </div>
      </section>

      <section className="de-faq" id="diamond-faq">
        <div className="container de-faq-grid">
          <div className="de-faq-title">
            <span className="de-kicker">06 · FAQ</span>
            <h2>שאלות שחוזרות לפני שבוחרים יהלום</h2>
            <p>תשובות קצרות וברורות — לפני שנכנסים לפרטים האישיים של האבן שלכם.</p>
          </div>
          <div className="de-faq-list">
            {faqs.map(([q,a],i)=><details key={q} open={i===0}>
              <summary>{q}<span>+</span></summary>
              <p>{a}</p>
            </details>)}
          </div>
        </div>
      </section>

      <section className="de-final">
        <div className="container de-final-grid">
          <div>
            <span>PRIVATE DIAMOND CONSULTATION</span>
            <h2>רוצים שנעבור יחד על היהלום שמתאים לכם?</h2>
            <p>נשווה בין אבנים, נסביר את הדו״ח ונבנה שילוב נכון של קראט, חיתוך, צבע וניקיון — בהתאם לעיצוב ולתקציב.</p>
          </div>
          <div className="de-final-actions">
            <a className="btn light" href="/appointments" onClick={e=>{e.preventDefault();go('/appointments')}}>לתיאום פגישה</a>
            <a className="btn outline" href={whatsapp} target="_blank" rel="noreferrer">ייעוץ ב־WhatsApp</a>
            <a className="de-text-link" href="/ring-builder" onClick={e=>{e.preventDefault();go('/ring-builder')}}>להתחיל לבנות טבעת ←</a>
          </div>
        </div>
      </section>
    </main>
  </>;
}
